import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import {
  InsuranceProviderFormData,
} from "../../interfaces/insuranceProviders";
import {
  createInsuranceProvider,
  updateInsuranceProvider,
} from "../../apis/insuranceProviders";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editableId?: number | null;
  providerData?: InsuranceProviderFormData | null;
}

const AddInsuranceProviderModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  editableId,
  providerData,
}) => {
  const defaultProvider: InsuranceProviderFormData = useMemo(
    () => ({
      name: "",
      policyNumberPrefix: "",
      contactPerson: "",
      phone: "",
      email: "",
      address: "",
    }),
    []
  );

  const [provider, setProvider] = useState<InsuranceProviderFormData>(defaultProvider);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const isEditMode = Boolean(editableId);

  useEffect(() => {
    if (!open) return;
    if (isEditMode && providerData) {
      setProvider({
        name: providerData.name ?? "",
        policyNumberPrefix: providerData.policyNumberPrefix ?? "",
        contactPerson: providerData.contactPerson ?? "",
        phone: providerData.phone ?? "",
        email: providerData.email ?? "",
        address: providerData.address ?? "",
      });
    } else {
      setProvider(defaultProvider);
    }
    setErrors({});
  }, [open, isEditMode, providerData, defaultProvider]);

  const validateForm = () => {
    const e: typeof errors = {};
    if (!provider.name?.trim()) e.name = "* Name is required";
    if (provider.email && provider.email.trim()) {
      // simple email validation; backend should be source of truth
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(provider.email.trim());
      if (!ok) e.email = "* Enter a valid email";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const normalizePayload = (p: InsuranceProviderFormData): InsuranceProviderFormData => ({
    name: p.name.trim(),
    policyNumberPrefix: p.policyNumberPrefix?.trim() ? p.policyNumberPrefix.trim() : null,
    contactPerson: p.contactPerson?.trim() ? p.contactPerson.trim() : null,
    phone: p.phone?.trim() ? p.phone.trim() : null,
    email: p.email?.trim() ? p.email.trim() : null,
    address: p.address?.trim() ? p.address.trim() : null,
  });

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = normalizePayload(provider);
    try {
      if (isEditMode) {
        await updateInsuranceProvider(editableId!, payload);
      } else {
        await createInsuranceProvider(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.log("Error saving insurance provider:", error);
    }
  };

  if (!open) return null;

  return (
    <Modal
      title={isEditMode ? "Edit Insurance Provider" : "Add Insurance Provider"}
      onClose={onClose}
      width="sm:max-w-2xl"
      modalContent={
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label>Name</label>
              <input
                type="text"
                value={provider.name}
                onChange={(e) => setProvider({ ...provider, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
              {errors.name && (
                <span className="text-sm text-red-500">{errors.name}</span>
              )}
            </div>

            <div>
              <label>Policy Number Prefix</label>
              <input
                type="text"
                value={provider.policyNumberPrefix ?? ""}
                onChange={(e) =>
                  setProvider({ ...provider, policyNumberPrefix: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label>Contact Person</label>
              <input
                type="text"
                value={provider.contactPerson ?? ""}
                onChange={(e) =>
                  setProvider({ ...provider, contactPerson: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label>Phone</label>
              <input
                type="text"
                value={provider.phone ?? ""}
                onChange={(e) => setProvider({ ...provider, phone: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>

            <div>
              <label>Email</label>
              <input
                type="email"
                value={provider.email ?? ""}
                onChange={(e) => setProvider({ ...provider, email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
              {errors.email && (
                <span className="text-sm text-red-500">{errors.email}</span>
              )}
            </div>

            <div className="sm:col-span-2">
              <label>Address</label>
              <textarea
                value={provider.address ?? ""}
                onChange={(e) => setProvider({ ...provider, address: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={handleSave}
              className="px-3 py-2 bg-primary text-white rounded"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
            <button onClick={onClose} className="px-3 py-2 border rounded">
              Cancel
            </button>
          </div>
        </div>
      }
    />
  );
};

export default AddInsuranceProviderModal;

